import { Trans, useLingui } from "@lingui/react/macro";
import type { FirstDay, FirstDayItem, Tool } from "@rakazo/contracts";
import { FIRST_DAY_ITEMS, TOOL_NAMES } from "@rakazo/contracts";
import { Button, cn } from "@rakazo/ui-web";
import { Check, X } from "lucide-react";
import { useEffect, useState } from "react";
import { rpc } from "../../lib/rpc";

/**
 * The first-day checklist after Chief's messages for an admin whose team the wizard made. Each item is
 * read from real state on the server and checked again whenever the window regains focus.
 */
export function FirstDayChecklist({
  activeBotId,
  onWizard,
  onConnect,
  onInvite,
  onInbox,
  onShownChange,
}: {
  activeBotId: string | null;
  /** The wizard is unfinished: the app goes back to it. */
  onWizard: () => void;
  /** Opens the app catalog with the company's own apps offered first. */
  onConnect: (apps: Tool[]) => void;
  onInvite: () => void;
  onInbox: () => void;
  /** Whether the checklist shows, so Chief's composer can invite the first task meanwhile. */
  onShownChange?: (shown: boolean) => void;
}) {
  const { t } = useLingui();
  const [day, setDay] = useState<FirstDay | null>(null);

  useEffect(() => {
    let live = true;
    const load = () =>
      void rpc.company
        .firstDay()
        .then((next) => {
          if (!live) return;
          if (next.wizard) onWizard();
          setDay(next);
        })
        .catch(() => undefined);
    load();
    window.addEventListener("focus", load);
    return () => {
      live = false;
      window.removeEventListener("focus", load);
    };
  }, []);

  const shown = Boolean(day?.visible && day.chiefId && day.chiefId === activeBotId);
  useEffect(() => onShownChange?.(shown), [shown]);

  if (!day || !shown) return null;

  type Row = {
    key: string;
    done: boolean;
    label: string;
    action?: { label: string; run: () => void };
  };
  const connect = t`Connect`;
  const items: Record<FirstDayItem, Omit<Row, "key" | "done">> = {
    team: { label: t`Create your team` },
    app: {
      label: t`Connect your first app`,
      action: { label: connect, run: () => onConnect(day.apps) },
    },
    invite: { label: t`Invite a colleague`, action: { label: t`Invite`, run: onInvite } },
    decision: {
      label: t`Approve your first decision`,
      action: { label: t`Open inbox`, run: onInbox },
    },
  };
  // The apps ticked at setup each get their own item in place of the generic one.
  const rows: Row[] = FIRST_DAY_ITEMS.flatMap((key): Row[] =>
    key === "app" && day.connect.length
      ? day.connect.map(({ tool, done }) => {
          const name = TOOL_NAMES[tool];
          return {
            key: `app-${tool}`,
            done,
            label: t`Connect ${name}`,
            action: { label: connect, run: () => onConnect([tool]) },
          };
        })
      : [{ key, done: day.done[key], ...items[key] }],
  );
  const dismiss = () => {
    setDay({ ...day, visible: false });
    void rpc.company.dismissFirstDay().catch(() => undefined);
  };

  return (
    <section
      data-testid="first-day-checklist"
      aria-label={t`First day`}
      className="w-full shrink-0"
    >
      <div className="rounded-xl border border-border bg-card p-4">
        <div className="mb-3 flex items-center justify-between gap-3">
          <h2 className="font-medium text-foreground">
            <Trans>Your team is live</Trans>
          </h2>
          <Button size="icon-sm" variant="ghost" aria-label={t`Dismiss`} onClick={dismiss}>
            <X />
          </Button>
        </div>
        <ul className="grid gap-2">
          {rows.map(({ key, done, label, action }) => {
            return (
              <li
                key={key}
                data-done={done || undefined}
                className="flex min-h-11 items-center gap-3 rounded-lg border border-border px-3 py-2"
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    "grid size-5 shrink-0 place-items-center rounded-full border",
                    done ? "border-success text-success" : "border-border",
                  )}
                >
                  {done ? <Check className="size-3" strokeWidth={3} /> : null}
                </span>
                <span
                  className={cn(
                    "flex-1 text-sm",
                    done ? "text-muted-foreground line-through" : "text-foreground",
                  )}
                >
                  {label}
                  {done ? <span className="sr-only">{t`, done`}</span> : null}
                </span>
                {!done && action ? (
                  <Button
                    size="sm"
                    variant="outline"
                    aria-label={key.startsWith("app-") ? label : undefined}
                    onClick={action.run}
                  >
                    {action.label}
                  </Button>
                ) : null}
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
